// src/utils/onboardingRouter.js
import { auth, db } from "../firebase/config";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";

/**
 * Look up the user's Stripe subscription status from Firestore:
 * customers/{uid}/subscriptions (extension) — pick the newest / active one.
 */
async function getSubscriptionStatus(uid) {
  try {
    const subsRef = collection(db, "customers", uid, "subscriptions");
    const subsSnap = await getDocs(subsRef);
    if (subsSnap.empty) return "none";

    // pick any active/trialing first; else first doc status
    let status = "none";
    subsSnap.forEach((d) => {
      const s = (d.data()?.status || "").toLowerCase();
      if (["active", "trialing"].includes(s)) status = s;
      else if (status === "none") status = s || "incomplete";
    });
    return status;
  } catch {
    return "none";
  }
}

function isPilotActive(company, userEmail) {
  const pilot = company?.pilot;
  if (!pilot?.enabled) return false;
  if ((pilot?.status || "").toLowerCase() !== "active") return false;

  const endsAtMs = pilot?.endsAt?.toMillis?.() ?? 0;
  if (!endsAtMs || Date.now() > endsAtMs) return false;

  const allow = (pilot?.allowListEmail || "").toLowerCase().trim();
  if (allow && (userEmail || "").toLowerCase().trim() !== allow) return false;

  return true;
}
/**
 * Decide which page/path the employer should see right now.
 * Returns a pathname (string) or null if no redirect needed.
 */
export async function resolveEmployerOnboardingPath() {
  const user = auth.currentUser;
  if (!user) return null;

  await user.reload();

  // 1) Look up the "users" doc to determine role
  const userRef = doc(db, "users", user.uid);
  const userSnap = await getDoc(userRef);

  // If there's no users doc, this is almost certainly an employee.
  // ✅ Do NOT hijack the flow — let employee logic (Login.js) handle it.
  if (!userSnap.exists()) return null;

  const u = userSnap.data() || {};
  const role = u.role || "employer";

  // 2) Only employers are gated here
  if (role !== "employer") return null;

  // 3) Employers must verify email
  if (!user.emailVerified) return "/verify-email";

  // 4) Company setup first
  const hasCompany = !!u.companyId;
  if (!hasCompany) return "/company-setup";

  // ✅ PILOT BYPASS: if company pilot is active, skip subscription gate
  const companyRef = doc(db, "companies", u.companyId);
  const companySnap = await getDoc(companyRef);
  const company = companySnap.exists() ? companySnap.data() : null;

  if (isPilotActive(company, user.email)) {
    return null; // allow access (no redirect to /subscription)
  }

  // 5) Subscription check
  // 5) Check the company subscription first
  const companySubscription = company?.subscription || null;

  const companyStatus = String(companySubscription?.status || "").toLowerCase();

  // Free users and active paid users can access the dashboard
  if (["free", "active", "trialing"].includes(companyStatus)) {
    return null;
  }

  // Optional Stripe fallback for existing paid subscriptions
  const stripeStatus = await getSubscriptionStatus(user.uid);

  if (["active", "trialing"].includes(stripeStatus)) {
    return null;
  }

  // No plan selected or subscription is inactive
  return "/subscription";
}
